// client/src/components/Logs/CreateWorkOrderFromLog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Box,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const createWorkOrder = async (data) => {
  const response = await axios.post('/workorders', data);
  return response.data;
};

const fetchMechanics = async () => {
  const response = await axios.get('/users?role=mechanic');
  return response.data;
};

const CreateWorkOrderFromLog = ({ open, log, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'maintenance',
    clientId: '',
    vehicleId: '',
    deviceId: '',
    mechanicId: '',
    startTime: new Date(),
    endTime: new Date(new Date().setHours(new Date().getHours() + 2)),
    notes: '',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const { data: mechanics, isLoading: mechanicsLoading } = useQuery({
    queryKey: ['mechanics'],
    queryFn: fetchMechanics,
    enabled: open,
  });

  // Заполнение формы данными из Log
  useEffect(() => {
    if (log && open) {
      setForm({
        title: log.title || '',
        description: log.description || '',
        type: 'maintenance',
        clientId: log.clientId?._id || log.clientId || '',
        vehicleId: log.vehicleId?._id || log.vehicleId || '',
        deviceId: log.deviceId?._id || log.deviceId || '',
        mechanicId: log.assignedTo?._id || log.assignedTo || '',
        startTime: new Date(),
        endTime: new Date(new Date().setHours(new Date().getHours() + 2)),
        notes: `Created from Log entry: ${log.title}\n${log.notes || ''}`,
      });
    }
  }, [log, open]);

  const mutation = useMutation({
    mutationFn: createWorkOrder,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['workorders']);
      if (onSuccess) onSuccess(data);
      if (onClose) onClose();
    },
    onError: (err) => {
      console.error('Create work order error:', err);
      setError(err.response?.data?.message || 'Error creating work order');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.clientId || !form.vehicleId) {
      setError('Please fill required fields: Title, Client, and Vehicle');
      return;
    }
    mutation.mutate(form);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Work Order from Log</DialogTitle>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            {mechanicsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    label="Type"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                    <MenuItem value="installation">Installation</MenuItem>
                    <MenuItem value="removal">Removal</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Mechanic</InputLabel>
                    <Select
                      value={form.mechanicId}
                      onChange={(e) => setForm({ ...form, mechanicId: e.target.value })}
                      label="Mechanic"
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {mechanics?.map((mechanic) => (
                        <MenuItem key={mechanic._id} value={mechanic._id}>
                          {mechanic.fullName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Client ID"
                    value={form.clientId}
                    disabled
                    helperText="Auto-filled from Log"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Vehicle ID"
                    value={form.vehicleId}
                    disabled
                    helperText="Auto-filled from Log"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Device ID"
                    value={form.deviceId || 'Not specified'}
                    disabled
                    helperText="Auto-filled from Log"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <DateTimePicker
                    label="Start Time"
                    value={form.startTime}
                    onChange={(v) => setForm({ ...form, startTime: v })}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <DateTimePicker
                    label="End Time"
                    value={form.endTime}
                    onChange={(v) => setForm({ ...form, endTime: v })}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating...' : 'Create Work Order'}
            </Button>
          </DialogActions>
        </form>
      </LocalizationProvider>
    </Dialog>
  );
};

export default CreateWorkOrderFromLog;